'use es6';

import quickFetch from 'quick-fetch';
const FORM_URL = `forms/v2/forms`;
const FORM_DRAFT_URL = `forms/v2/forms-drafts`;
const FEEDBACK_SURVEY_URL = `feedback/v1/custom-survey/survey-for-form`;
const FORM_SUBMISSIONS_TABLE_URL = `submissions/app/v1/submission-table`;
const FORM_SINGLE_SUBMISSION_URL = `submissions/app/v1/submission-details`;
const PROPERTY_FREQUENCY_URL = `submissions/app/v1/submission-report`;
const FORM_PAGE_SUBMISSION_URL = `submissions/app/v1/submission-page/initialize`;
const FORM_SUBMISSION_ANALYTICS_URL = `analytics/v2/forms/total`;
const FORMS_EDITOR_CUSTOM_OBJECTS = `forms/app/v1/form-editor/properties`;
const FORMS_EDITOR_THEMES = `forms/v2/themes`;
const USER_PREFERENCES_URL = `userpreferences/v1`;
const BUSINESS_UNITS_URL = `business-units/v1/business-unit-displays/user/dropdown`;
const HUB_USERS_URL = 'users/v2/app';
const EMAIL_OWNERSHIP_URL = 'email-ownership/v1';
const TEAMS_URL = 'app-users/v1/teams';
const NOTIFICATION_PREFERENCES_URL = 'notification/preferences/v2';
const TEMPLATES_URL = `forms/templates.json`;
const FLOWS_URL = 'automationapps/v1';
const WEBSITE_FLOWS_URL = 'content-workflows/v1/followup-email/pages-by-form';
const SALES_CHECKOUT_BASE_URL = '/sales-checkout/v2';
const PAYMENT_ELIGIBILITY_BASE_URL = '/payments/v1';
const DATA_PRIVACY_SETTINGS_URL = 'data-privacy-settings/hub-settings';
const CRM_SEARCH = 'crm-search/search';
const FORM_ROUTING_DRAFT = 'forms-routing/v1/route/draft';
const PORTAL_SCOPES = 'hubs2/v1/scopes/hub';
const FILTER_GROUPS_MAP = {
  '0-105': []
};
const DEFAULT_CRM_SEARCH_FOR_TOTAL_QUERY = {
  associationPreviews: {},
  count: 0,
  filterGroups: [],
  objectTypeId: '0-28',
  offset: 0,
  query: '',
  requestOptions: {
    allPropertiesFetchMode: 'latest_version',
    includeAllProperties: true
  }
};
const defaultNotificationPreferencesQuery = {
  locale: 'en'
};
const serializeValue = (key, value) => {
  return `${encodeURIComponent(key)}=${encodeURIComponent(value == null ? '' : value)}`;
};
const serializeArray = (key, value) => {
  return value.reduce((result, current) => {
    if (current != null) {
      result.push(serializeValue(`${key}`, current));
    }
    return result;
  }, []).join('&');
};
const buildQueryParams = query => {
  if (query == null) return '';
  if (typeof query === 'string' || query instanceof String) return query;
  return Object.keys(query).reduce((result, key) => {
    const value = query[key];
    if (Array.isArray(value)) {
      if (value.length > 0) result.push(serializeArray(key, value));
    } else if (value != null) {
      result.push(serializeValue(key, value));
    }
    return result;
  }, []).join('&');
};
const buildUrl = (endpoint, queryData = {}) => {
  const query = queryData ? `?${buildQueryParams(queryData)}` : '';
  return quickFetch.getApiUrl(`/${endpoint}${query}`, true);
};
export const getBootstrapPropertiesKey = () => `FormsEditor:BoostrapProperties`;
export const getThemesKey = () => `FormsEditor:Themes`;
export const getPropertyFrequencyKey = (formGuid = '') => `FormsSubmissions:Analyze:${formGuid}`;
export const getSubmissionBootstrapKey = (formGuid = '') => `FormsSubmissions:Bootstrap:${formGuid}`;
export const getCountSubmissionsKey = (formGuid = '') => `FormsSubmissions:CountSubmissions:${formGuid}`;
export const getFormAnalyticsKey = (formGuid = '') => `FormsSubmissions:FormAnalytics:${formGuid}`;
const DEFAULT_SORT_COLUMN = {
  column: '0-1/LAST_MODIFIED',
  order: 'DESC'
};
const DEFAULT_FIELD_VALUE = undefined;
export function getFormSubmissionsTableKey(id = '', {
  dateType,
  startDate,
  endDate
}, extraColumns = [], pageNumber = 1, fieldValueFilter = DEFAULT_FIELD_VALUE, sortColumn = DEFAULT_SORT_COLUMN) {
  let dateValue;
  if (!!dateType && dateType !== 'CUSTOM') {
    dateValue = dateType;
  } else if (startDate && endDate) {
    dateValue = `${startDate}:${endDate}`;
  } else {
    dateValue = 'LAST_THIRTY_DAYS';
  }
  return `FormSubmissionsTable:${id}:${dateValue}:${JSON.stringify(extraColumns)}:${pageNumber}:${JSON.stringify(fieldValueFilter)}:${JSON.stringify(sortColumn)}`;
}
export function getFormKey(id = '') {
  return `FormDefinition:${id}`;
}
export function getContactPropertiesKey() {
  return 'ContactProperties';
}
export function getTicketPropertiesKey() {
  return 'TicketProperties';
}
export function getFormDraftKey(id = '') {
  return `FormDraft:${id}`;
}
export function getFeedbackKey(id = '') {
  return `FeedbackSurvey:${id}`;
}
export function getFormRoutingKey(id = '') {
  return `FormRouting:${id}`;
}
export const getHubUsersKey = () => 'HubUsers';
export const getOwnedAddressesKey = () => 'OwnedAddresses';
export const getTeamsKey = () => 'Teams';
export const getNotificationPreferencesKey = () => 'NotificationPreferences';
export const getDefaultUserPreferencesKey = () => 'DefaultUserPreferences';
export const getBusinessUnitsKey = () => 'BusinessUnits';
export const getTemplatesKey = () => 'Templates';
export const getPaymentSessionTemplatesKey = () => 'PaymentSessionTemplates';
export const getPaymentEligibilityKey = () => 'PaymentEligibility';
export const getPortalScopesKey = () => 'PortalScopes';
const getFlowsKey = () => 'Flows';
const getWebsiteFlowsKey = () => 'WebsiteFlows';
export const getCRMSearchForTotalKey = objectTypeId => `CRMSearchForTotal:${objectTypeId}`;
export function getColumnConfigKey(userId, formGuid) {
  return `ColumnConfig:${userId}:${formGuid}`;
}
export const getFormSubmissionKey = submissionId => `FormSubmission:${submissionId}`;
function authCheck(authData, scopes = [], gates = []) {
  let scopeCheck = false;
  let gateCheck = false;
  if (authData && authData.user && authData.user.scopes) {
    scopeCheck = scopes.every(scope => authData.user.scopes.includes(scope));
  }
  if (authData && authData.portal && authData.portal.enabled_gates) {
    gateCheck = gates.every(gate => authData.portal.enabled_gates.includes(gate));
  }
  return scopeCheck && gateCheck;
}
function earlyRequestFetch(url, responseKey, options = {}) {
  quickFetch.makeEarlyRequest(responseKey, Object.assign({
    url,
    dataType: 'json',
    xhrFields: {
      withCredentials: true
    },
    timeout: 15000
  }, options));
}
function earlyRequestFetchWithAuth(url, responseKey, options = {}, scopes = [], gates = []) {
  quickFetch.afterAuth(authData => {
    if (authCheck(authData, scopes, gates)) {
      earlyRequestFetch(url, responseKey, options);
    }
  });
}
export const getDataPrivacySettingsKey = () => `DataPrivacySettings`;
export const businessUnitFetch = () => {
  earlyRequestFetchWithAuth(buildUrl(BUSINESS_UNITS_URL), getBusinessUnitsKey(), {}, ['business-units-access']);
};
export const settingsFetch = () => {
  const hubUsersUrl = buildUrl(`${HUB_USERS_URL}/hub-users`);
  const ownedAddresses = buildUrl(`${EMAIL_OWNERSHIP_URL}/owned-addresses`);
  const teamsUrl = buildUrl(`${TEAMS_URL}`);
  const notificationPreferencesUrl = () => {
    return buildUrl(`${NOTIFICATION_PREFERENCES_URL}/preferences`, defaultNotificationPreferencesQuery);
  };
  earlyRequestFetch(hubUsersUrl, getHubUsersKey());
  earlyRequestFetch(ownedAddresses, getOwnedAddressesKey());
  earlyRequestFetch(teamsUrl, getTeamsKey());
  quickFetch.afterAuth(( /*authData*/
  ) => {
    earlyRequestFetch(notificationPreferencesUrl(), getNotificationPreferencesKey());
  });
};
export function dashboardFetch() {
  ['0-105'].forEach(objectTypeId => {
    earlyRequestFetch(buildUrl(CRM_SEARCH), getCRMSearchForTotalKey(objectTypeId), {
      type: 'POST',
      data: JSON.stringify(Object.assign({}, DEFAULT_CRM_SEARCH_FOR_TOTAL_QUERY, {
        filterGroups: FILTER_GROUPS_MAP[objectTypeId],
        objectTypeId
      }))
    });
  });
}
export function editorFetch(guid) {
  if (!guid) {
    return;
  }
  const feedbackSurveyUrl = buildUrl(`${FEEDBACK_SURVEY_URL}/${guid}`);
  const formUrl = buildUrl(`${FORM_DRAFT_URL}/${guid}`);
  const bootstrapUrl = buildUrl(`${FORMS_EDITOR_CUSTOM_OBJECTS}/initialize`);
  const themesUrl = buildUrl(`${FORMS_EDITOR_THEMES}`);
  const userPreferencesUrl = buildUrl(`${USER_PREFERENCES_URL}/attributes`);
  const paymentSessionTemplates = buildUrl(`${SALES_CHECKOUT_BASE_URL}/session-templates/active`);
  const formRoutingUrl = buildUrl(`${FORM_ROUTING_DRAFT}/${guid}`);
  earlyRequestFetch(formUrl, getFormDraftKey(guid));
  earlyRequestFetch(feedbackSurveyUrl, getFeedbackKey(guid));
  earlyRequestFetch(bootstrapUrl, getBootstrapPropertiesKey());
  earlyRequestFetch(themesUrl, getThemesKey());
  earlyRequestFetch(userPreferencesUrl, getDefaultUserPreferencesKey());
  earlyRequestFetchWithAuth(paymentSessionTemplates, getPaymentSessionTemplatesKey(), {}, ['payment-links-read']);
  earlyRequestFetchWithAuth(formRoutingUrl, getFormRoutingKey(guid), {}, ['forms-routing-access']);
  businessUnitFetch();
}
export const templatesFetch = () => {
  const bootstrapUrl = buildUrl(`${FORMS_EDITOR_CUSTOM_OBJECTS}/initialize`);
  const themesUrl = buildUrl(`${FORMS_EDITOR_THEMES}`);
  const templatesUrl = buildUrl(`${TEMPLATES_URL}`);
  earlyRequestFetch(templatesUrl, getTemplatesKey());
  earlyRequestFetch(themesUrl, getThemesKey());
  earlyRequestFetch(bootstrapUrl, getBootstrapPropertiesKey());
};
export const submissionsFetchSubmission = submissionId => {
  const singleSubmissionUrl = buildUrl(`${FORM_SINGLE_SUBMISSION_URL}/${submissionId}`);
  earlyRequestFetch(singleSubmissionUrl, getFormSubmissionKey(submissionId));
};
export const submissionsFetch = guid => {
  if (!guid) {
    return;
  }
  const url = buildUrl(`${FORM_PAGE_SUBMISSION_URL}/${guid}`);
  earlyRequestFetch(url, getSubmissionBootstrapKey(guid));
};
export const automationFetch = (guid, portalId) => {
  if (!guid) {
    return;
  }
  const flowsUrl = buildUrl(`${FLOWS_URL}/summaries`, {
    portalId,
    platformSourceApps: 'FORMS_APP',
    uuid: `${portalId}:${guid}`
  });
  const websiteFlowsUrl = buildUrl(`${WEBSITE_FLOWS_URL}/${guid}`);
  earlyRequestFetch(flowsUrl, getFlowsKey(guid));
  earlyRequestFetch(websiteFlowsUrl, getWebsiteFlowsKey(guid));
};
const getDateParamFromObjectAndQueryParams = (paramName, dateType) => {
  const urlParams = new URLSearchParams(window.location.search);
  const dateTypeParam = urlParams.get(paramName);
  return dateTypeParam || dateType[paramName].toISOString().split('T')[0];
};
export const portalScopesFetch = portalId => {
  const portalScopesUrl = buildUrl(`${PORTAL_SCOPES}/${portalId}`);
  earlyRequestFetch(portalScopesUrl, getPortalScopesKey());
};
export function formSubmissionsTableFetch(guid, dateType = {
  dateType: 'LAST_THIRTY_DAYS',
  startDate: new Date(Date.now() - 30 * 24 * 3600 * 1000),
  endDate: new Date()
}) {
  if (!guid) {
    return;
  }
  const url = buildUrl(FORM_SUBMISSIONS_TABLE_URL);
  const startDate = getDateParamFromObjectAndQueryParams('startDate', dateType);
  const endDate = getDateParamFromObjectAndQueryParams('endDate', dateType);
  const urlParams = new URLSearchParams(window.location.search);
  const dateTypeParam = urlParams.get('dateType');
  earlyRequestFetch(url, getFormSubmissionsTableKey(guid, {
    dateType: dateTypeParam === 'ALL' ? dateTypeParam : dateType.dateType,
    startDate,
    endDate
  }, []), {
    type: 'POST',
    data: JSON.stringify({
      formGuid: guid,
      startDate: dateTypeParam === 'ALL' ? '2000-01-01' : startDate,
      endDate,
      limit: 10,
      offset: 0
    })
  });
}
export function performanceFetch(guid) {
  if (!guid) {
    return;
  }
  const formDefinitionUrl = buildUrl(`${FORM_URL}/${guid}`);
  earlyRequestFetch(formDefinitionUrl, getFormKey(guid));
}
export function performanceEarlyRequestCompleted(guid) {
  const formRequest = quickFetch.getRequestStateByName(getFormKey(guid)) || {};
  return formRequest.finished;
}
export function submissionsEarlyRequestCompleted(guid) {
  const formRequest = quickFetch.getRequestStateByName(getFormKey(guid)) || {};
  return formRequest.finished;
}
export function editorEarlyRequestCompleted(guid) {
  const formDefinitionRequest = quickFetch.getRequestStateByName(`FormDraft:${guid}`) || {};
  return formDefinitionRequest.finished;
}
export const propertyFrequencyBucketsFetch = guid => {
  const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const endDate = new Date();
  const url = buildUrl(`${PROPERTY_FREQUENCY_URL}/${guid}/field-totals`, {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    includeRequiredFields: true
  });
  earlyRequestFetch(url, getPropertyFrequencyKey(guid));
};
export const propertyFrequencyBucketsRequestCompleted = guid => {
  const formRequest = quickFetch.getRequestStateByName(getPropertyFrequencyKey(guid));
  return formRequest && formRequest.finished ? formRequest.finished : false;
};
export const countSubmissionsFetch = guid => {
  const currentDate = new Date();
  const date30DaysAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000);
  const urlCountSubmissions = buildUrl(`${PROPERTY_FREQUENCY_URL}/${guid}/totals`, {
    startDate: date30DaysAgo.toISOString().split('T')[0],
    endDate: currentDate.toISOString().split('T')[0],
    includeRequiredFields: true
  });
  const urlSubmissionAnalytics = buildUrl(`${FORM_SUBMISSION_ANALYTICS_URL}`, {
    d1: guid,
    start: new Date(date30DaysAgo).toISOString().slice(0, 10).replace(/-/g, ''),
    end: currentDate.toISOString().slice(0, 10).replace(/-/g, '')
  });
  earlyRequestFetch(urlCountSubmissions, getCountSubmissionsKey(guid));
  earlyRequestFetch(urlSubmissionAnalytics, getFormAnalyticsKey(guid));
};
export const paymentSessionTemplatesFetch = () => {
  const paymentEligibilityUrl = buildUrl(`${PAYMENT_ELIGIBILITY_BASE_URL}/payment-accounts/eligibility`);
  earlyRequestFetch(paymentEligibilityUrl, getPaymentEligibilityKey());
};
export const dataPrivacySettingsFetch = ({
  portalId
}) => {
  earlyRequestFetch(buildUrl(`${DATA_PRIVACY_SETTINGS_URL}`, {
    portalId
  }), getDataPrivacySettingsKey());
};
export const localRequestsOverrides = isLocalHost => {
  const emptyFn = () => {};
  try {
    if (isLocalHost) {
      window.localStorage.setItem('LOCAL_API_OVERRIDES', JSON.stringify([`/${TEMPLATES_URL}`]));
    } else {
      window.localStorage.removeItem('LOCAL_API_OVERRIDES');
    }
  } catch (e) {
    emptyFn();
  }
};